/**
 * reportFindings — a small, schema-aware reader for a SiteRagReport's
 * conclusion findings.
 *
 * The platform's report schemas diverge between report types: environmental
 * reports persist their headline findings under `topConstraints` (regulatory
 * / permitting constraints) where feasibility / power-availability /
 * water-resource / test-fit / interconnection use `topRisks` (development
 * risks). A read site that only looks at `topRisks` therefore renders an
 * empty list on a fully-populated environmental report.
 *
 * This module centralises the multi-field fallback so the AtAGlance panel
 * (TF1) and the dashboard site-card summary (DC3) agree:
 *
 *   topRisks → topConstraints → topStrengths → keyConditions
 *     → developmentRisks → executiveSummary / summary
 *
 * The first four layers read the LLM-authored conclusion arrays. The last
 * two (FE1) cover the failure mode where the conclusion is empty but the
 * report still has useful content elsewhere — `developmentRisks[]` is the
 * deterministic risk register populated by data providers (it survives an
 * empty LLM conclusion), and `executiveSummary` / `summary` are prose blobs
 * we can lift a one-liner from as a last resort.
 *
 * Each layer is trimmed and emptied of blank entries before it counts.
 *
 * Pure, prop-driven, no store or React dependency — unit-testable.
 */
import type { SiteRagReport } from '../types/siteRag';
import type { ReportTypeId } from '../config/reportTypes';
import { coerceNarrativeText } from './safeNarrativeProse';

/** Which conclusion array a set of findings was lifted from. */
export type FindingsSource =
  | 'risks'
  | 'constraints'
  | 'strengths'
  | 'conditions'
  | 'devRisks'
  | 'summary'
  | 'none'
  // FS1 — strengths-side fallback sources. `topStrengths` is the
  // preferred LLM array (alias of `strengths` for the strengths reader),
  // `keyOpportunities` is the same-shape companion field, and
  // `developmentStrengths` is the deterministic register that mirrors
  // `developmentRisks` for the favorable side.
  | 'topStrengths'
  | 'keyOpportunities'
  | 'developmentStrengths';

export interface ReportFindings {
  /** Up to `limit` trimmed, non-empty finding strings (may be empty). */
  items: string[];
  /** Which conclusion array `items` came from (`'none'` when all empty). */
  source: FindingsSource;
}

type Conclusion = NonNullable<SiteRagReport['conclusion']>;

/** Trim, drop blanks, cap. */
function clean(items: readonly string[] | undefined, limit: number): string[] {
  return (items ?? [])
    .filter((s): s is string => typeof s === 'string' && s.trim().length > 0)
    .map(s => s.trim())
    .slice(0, limit);
}

/**
 * Cap a single finding string so a long developmentRisk reason or summary
 * sentence doesn't blow out the panel layout. Adds an ellipsis when truncated.
 *
 * Use ONLY on surfaces that genuinely can't fit the full text (tight
 * side-panels, inline chips). Overview cards and report bodies should
 * render the full text — pass `Number.POSITIVE_INFINITY` (or
 * `truncateAt: 'none'` via the reader-level options) to disable.
 */
function cap(str: string, maxLen: number): string {
  const t = str.trim();
  if (!Number.isFinite(maxLen) || t.length <= maxLen) return t;
  return `${t.slice(0, maxLen).trimEnd()}…`;
}

/**
 * OV3 — opt-in truncation for the findings readers.
 *
 * Historically `readReportFindings` and `readReportStrengths` hard-capped
 * every item (development-risk reason at 120 chars, summary fallback at
 * 200) with `…`. That was fine for the at-a-glance card's tight
 * `border bg-state-hover px-2.5 py-1.5` chips but wrong for the Site
 * Landing Overview's spacious Risks/Strengths cards, which had plenty of
 * vertical space to render the full reason.
 *
 * Callers now opt in to truncation explicitly:
 *   - `truncateAt: undefined`  → default 120/200 char caps (back-compat).
 *   - `truncateAt: 'none'`     → render full text, no ellipsis.
 *   - `truncateAt: <number>`   → custom per-item cap (also applies to
 *                                the summary-fallback layer when the
 *                                custom cap is tighter than 200).
 *
 * Visual clamping (the line-clamp-3 + "Show more" toggle introduced in
 * OV4) is the better long-text rhythm and respects responsive width;
 * `truncateAt` is the data-layer escape hatch for "render the data
 * unaltered so the visual layer can decide what to clamp."
 */
export interface ReadFindingsOptions {
  /** Per-item character cap. Omit (or pass `undefined`) to keep the
   *  legacy 120-char default; pass `'none'` to render the full text. */
  truncateAt?: number | 'none';
}

/** Resolve `ReadFindingsOptions.truncateAt` to a numeric cap. */
function resolveItemCap(opts: ReadFindingsOptions | undefined, fallback: number): number {
  if (!opts) return fallback;
  if (opts.truncateAt === 'none') return Number.POSITIVE_INFINITY;
  if (typeof opts.truncateAt === 'number' && Number.isFinite(opts.truncateAt) && opts.truncateAt > 0) {
    return opts.truncateAt;
  }
  return fallback;
}

/**
 * Read the headline findings off a report's conclusion, walking the
 * schema-divergence fallback chain. `report` may be `undefined` (no report
 * yet) → `{ items: [], source: 'none' }`.
 *
 * `limit` defaults to 3 (the AtAGlance panel shows three).
 *
 * OV3 — `opts.truncateAt` controls per-item truncation. Default behaviour
 * (omit `opts` or pass `{ }`) keeps the legacy 120-char dev-risk cap +
 * 200-char summary cap. Pass `{ truncateAt: 'none' }` to render full
 * text (Overview cards). A custom number tightens the cap further.
 */
export function readReportFindings(
  report: SiteRagReport | undefined,
  limit = 3,
  opts: ReadFindingsOptions = {},
): ReportFindings {
  if (!report) return { items: [], source: 'none' };
  const c: Conclusion | undefined = report.conclusion;

  // Steps 1-4: LLM-authored conclusion arrays (post-TF1).
  // These layers are already short LLM-curated bullets — `clean()` does
  // trim/blank-drop but no length cap. `truncateAt` only affects the
  // fallback layers below (developmentRisks reasons + summary prose).
  if (c) {
    const risks = clean(c.topRisks, limit);
    if (risks.length > 0) return { items: risks, source: 'risks' };

    const constraints = clean(c.topConstraints, limit);
    if (constraints.length > 0) return { items: constraints, source: 'constraints' };

    const strengths = clean(c.topStrengths, limit);
    if (strengths.length > 0) return { items: strengths, source: 'strengths' };

    const conditions = clean(c.keyConditions, limit);
    if (conditions.length > 0) return { items: conditions, source: 'conditions' };
  }

  // FE1 — extended fallbacks for the empty-conclusion failure mode.

  // 5. developmentRisks — deterministic risk register from data providers.
  //    Survives an empty LLM conclusion (a real failure mode when the SED
  //    was stale at generation time and the LLM had garbage to work from).
  //    Format as "category: reason" so each row reads on its own.
  const devRiskCap = resolveItemCap(opts, 120);
  const devRisks = (report.developmentRisks ?? [])
    .filter(
      (r): r is NonNullable<typeof r> =>
        !!r && typeof r.category === 'string' && r.category.trim().length > 0
        && typeof r.reason === 'string' && r.reason.trim().length > 0,
    )
    .slice(0, limit)
    .map(r => `${r.category.trim()}: ${cap(r.reason, devRiskCap)}`);
  if (devRisks.length > 0) return { items: devRisks, source: 'devRisks' };

  // 6. executiveSummary / summary — last-resort prose. Lift the first
  //    sentence (split on .!? followed by whitespace) and cap to 200 chars
  //    by default. `truncateAt` overrides both layers; the summary cap
  //    floor is intentionally higher than the devRisk cap because the
  //    summary one-liner is the entire fallback content.
  const summary = coerceNarrativeText(report.executiveSummary).trim()
    || coerceNarrativeText(report.summary).trim();
  if (summary.length > 0) {
    const summaryCap = resolveItemCap(opts, 200);
    const oneLiner = cap(summary.split(/(?<=[.!?])\s+/)[0] ?? summary, summaryCap);
    if (oneLiner.length > 0) return { items: [oneLiner], source: 'summary' };
  }

  return { items: [], source: 'none' };
}

/**
 * Read risk-side conclusion items for the Conclusion section grid.
 * Walks only risk sources — does not fall through to strengths/conditions/summary.
 *
 *   topRisks → topConstraints → developmentRisks
 */
export function readConclusionRiskItems(
  report: SiteRagReport | undefined,
  opts: ReadFindingsOptions = {},
): ReportFindings {
  if (!report) return { items: [], source: 'none' };
  const c: Conclusion | undefined = report.conclusion;

  if (c) {
    const risks = clean(c.topRisks, Number.POSITIVE_INFINITY);
    if (risks.length > 0) return { items: risks, source: 'risks' };

    const constraints = clean(c.topConstraints, Number.POSITIVE_INFINITY);
    if (constraints.length > 0) return { items: constraints, source: 'constraints' };
  }

  const devRiskCap = resolveItemCap(opts, Number.POSITIVE_INFINITY);
  const devRisks = (report.developmentRisks ?? [])
    .filter(
      (r): r is NonNullable<typeof r> =>
        !!r && typeof r.category === 'string' && r.category.trim().length > 0
        && typeof r.reason === 'string' && r.reason.trim().length > 0,
    )
    .map(r => `${r.category.trim()}: ${cap(r.reason, devRiskCap)}`);
  if (devRisks.length > 0) return { items: devRisks, source: 'devRisks' };

  return { items: [], source: 'none' };
}

/**
 * The first non-empty trimmed entry the fallback chain yields, or
 * `undefined` when every layer is empty. Used by the site-card summary,
 * which only wants a one-line headline.
 */
export function firstReportFinding(report: SiteRagReport | undefined): string | undefined {
  const { items } = readReportFindings(report, 1);
  return items[0];
}

/**
 * Human label for a findings source, type-aware for the
 * risks-vs-constraints split (TF2): environmental reports surface
 * "Top constraints" (the term regulatory analysts use), everything else
 * "Top risks".
 */
export function findingsLabel(source: FindingsSource, reportType: ReportTypeId | undefined): string {
  switch (source) {
    case 'risks':
      return reportType === 'environmental' ? 'Top constraints' : 'Top risks';
    case 'constraints':
      return 'Top constraints';
    case 'strengths':
    case 'topStrengths':
      return 'Top strengths';
    case 'keyOpportunities':
      return 'Top opportunities';
    case 'developmentStrengths':
      return 'Top strengths (data-derived)';
    case 'conditions':
      return 'Key conditions';
    case 'devRisks':
      return 'Development risks';
    case 'summary':
      return 'Summary';
    case 'none':
      return 'Findings';
  }
}

/**
 * FS1 — read the headline strengths off a report's conclusion, walking
 * a fallback chain symmetric to `readReportFindings`:
 *
 *   topStrengths → keyOpportunities → developmentStrengths → summary
 *
 * Step 1 is the LLM-curated short bullets the conclusion was authored
 * with. Step 2 is the "opportunities" framing some report types prefer
 * (same shape, different field). Step 3 is the deterministic strengths
 * register populated by data providers (mirrors `developmentRisks`),
 * which survives an empty LLM conclusion. Step 4 lifts strength-keyword
 * sentences out of the executiveSummary / summary prose as a last
 * resort.
 *
 * Each layer is trimmed and emptied of blank entries before it counts.
 * `report` may be `undefined` (no report yet) → `{ items: [], source: 'none' }`.
 *
 * `limit` defaults to 3 (the SiteLandingPage panel shows three).
 */
export function readReportStrengths(
  report: SiteRagReport | undefined,
  limit = 3,
  opts: ReadFindingsOptions = {},
): ReportFindings {
  if (!report) return { items: [], source: 'none' };

  // 1. conclusion.topStrengths — preferred, LLM-curated short bullets.
  //    Already short — `truncateAt` only affects the layers below.
  const top = clean(report.conclusion?.topStrengths, limit);
  if (top.length > 0) return { items: top, source: 'topStrengths' };

  // 2. conclusion.keyOpportunities — same shape, different field; the
  //    "opportunities" framing some report types use.
  const opps = clean(report.conclusion?.keyOpportunities, limit);
  if (opps.length > 0) return { items: opps, source: 'keyOpportunities' };

  // 3. developmentStrengths — deterministic strengths register from data
  //    providers (mirrors `developmentRisks`). Format as
  //    "title — description"; drop rows missing both pieces.
  //    OV3: per-item cap configurable via `opts.truncateAt` (160 default).
  const devStrengthCap = resolveItemCap(opts, 160);
  const devStrengths = (report.developmentStrengths ?? [])
    .filter(
      (s): s is { title?: string; description?: string } =>
        !!s
        && (typeof s.title === 'string' || typeof s.description === 'string'),
    )
    .map(s => [s.title, s.description]
      .filter((p): p is string => typeof p === 'string' && p.trim().length > 0)
      .map(p => p.trim())
      .join(' – '))
    .filter(s => s.length > 0)
    .slice(0, limit)
    .map(s => cap(s, devStrengthCap));
  if (devStrengths.length > 0) return { items: devStrengths, source: 'developmentStrengths' };

  // 4. summary prose extraction — last-resort heuristic. Pick sentences
  //    that mention strength keywords; cap each at 200 chars by default.
  const summarySource = coerceNarrativeText(report.executiveSummary).trim()
    || coerceNarrativeText(report.summary).trim();
  const summaryStrengths = extractStrengthsFromSummary(summarySource, limit, resolveItemCap(opts, 200));
  if (summaryStrengths.length > 0) return { items: summaryStrengths, source: 'summary' };

  return { items: [], source: 'none' };
}

/**
 * Split `summary` on sentence boundaries and keep sentences that mention
 * strength keywords. Each surviving sentence is trimmed and capped at
 * `maxLen` chars (defaults to 200, matches the prose-extraction cap used
 * by `readReportFindings`). Pass `Number.POSITIVE_INFINITY` to disable.
 */
function extractStrengthsFromSummary(summary: string, limit: number, maxLen = 200): string[] {
  if (!summary) return [];
  const sentences = summary.split(/(?<=[.!?])\s+/);
  const keywords = /\b(strength|advantage|favorable|competitive|low-cost|abundant|excellent|strong|positive|opportunity|clean)\b/i;
  return sentences
    .map(s => s.trim())
    .filter(s => s.length > 20 && keywords.test(s))
    .slice(0, limit)
    .map(s => cap(s, maxLen));
}

/**
 * FS2 — human label for a strengths source. Mirrors `findingsLabel` but
 * scoped to the strengths-side source values and the environmental
 * report's "opportunities" framing preference.
 *
 * Environmental reports surface "Top opportunities" when the source is
 * the primary LLM array, matching how regulatory teams talk about the
 * favorable side. Everything else says "Top strengths" by default.
 * The deterministic and prose-derived layers get explicit suffixes so
 * the reader can tell where the bullets came from.
 */
export function strengthsLabel(source: FindingsSource, reportType: ReportTypeId | undefined): string {
  switch (source) {
    case 'developmentStrengths':
      return 'Top strengths (data-derived)';
    case 'summary':
      return 'Top strengths (from summary)';
    case 'keyOpportunities':
      return 'Top opportunities';
    case 'topStrengths':
    case 'strengths':
      return reportType === 'environmental' ? 'Top opportunities' : 'Top strengths';
    default:
      return 'Top strengths';
  }
}
